import { TooltipProvider } from "@/components/ui/tooltip";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { LanguageProvider } from "@/i18n/LanguageProvider";
import { canonicalizeLanguageLocation } from "@/lib/languageRouting";
import { HelmetProvider } from "react-helmet-async";
import { ScrollToTop } from "@/components/ScrollToTop";
import { MotionConfig } from "framer-motion";
import { lazy, Suspense, useEffect, useState } from "react";
import Index from "./pages/Index";

// Lazy-load notification components (not needed on initial render)
const Toaster = lazy(() => import("@/components/ui/toaster"));
const Sonner = lazy(() => import("@/components/ui/sonner"));

// Lazy-load all non-homepage routes
const ServicesHub = lazy(() => import("./pages/services/ServicesHub"));
const ServicePage = lazy(() => import("./pages/services/ServicePage"));
const HostingPage = lazy(() => import("./pages/services/HostingPage"));
const MobilityPage = lazy(() => import("./pages/services/MobilityPage"));
const SecurityPage = lazy(() => import("./pages/services/SecurityPage"));
const SupportPage = lazy(() => import("./pages/services/SupportPage"));
const HardwarePage = lazy(() => import("./pages/services/HardwarePage"));
const CommunicationPage = lazy(() => import("./pages/services/CommunicationPage"));
const BackupPage = lazy(() => import("./pages/services/BackupPage"));
const ConsultingPage = lazy(() => import("./pages/services/ConsultingPage"));
const InternetPage = lazy(() => import("./pages/services/InternetPage"));
const MobilePhonePage = lazy(() => import("./pages/services/MobilePhonePage"));
const TeamsPage = lazy(() => import("./pages/services/TeamsPage"));
const VoIPPage = lazy(() => import("./pages/services/VoIPPage"));
const ConnectivityPage = lazy(() => import("./pages/services/ConnectivityPage"));
const UnderConstruction = lazy(() => import("./pages/UnderConstruction"));
const ClientPortal = lazy(() => import("./pages/support/ClientPortal"));
const RemoteAssistance = lazy(() => import("./pages/support/RemoteAssistance"));
const SupportRequest = lazy(() => import("./pages/support/SupportRequest"));
const NotFound = lazy(() => import("./pages/NotFound"));
const History = lazy(() => import("./pages/about/History"));
const Team = lazy(() => import("./pages/about/Team"));
const Values = lazy(() => import("./pages/about/Values"));
const Technologies = lazy(() => import("./pages/about/Technologies"));
const ClientTestimonials = lazy(() => import("./pages/about/Testimonials"));
const Articles = lazy(() => import("./pages/about/Articles"));
const Press = lazy(() => import("./pages/about/Press"));
const BlogArticle = lazy(() => import("./pages/blog/BlogArticle"));
const KBCategoryPage = lazy(() => import("./pages/kb/KBCategoryPage"));
const KBArticlePage = lazy(() => import("./pages/kb/KBArticlePage"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const Contact = lazy(() => import("./pages/Contact"));
const Recruitment = lazy(() => import("./pages/Recruitment"));
const LegalNotice = lazy(() => import("./pages/LegalNotice"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const FAQ = lazy(() => import("./pages/FAQ"));
const TrustCenter = lazy(() => import("./pages/TrustCenter"));
const DiagnosticIt = lazy(() => import("./pages/DiagnosticIt"));
const HiddenItCostCalculator = lazy(() => import("./pages/HiddenItCostCalculator"));
const ItProviderComparison = lazy(() => import("./pages/ItProviderComparison"));
const SectorsHub = lazy(() => import("./pages/sectors/SectorsHub"));
const SectorPage = lazy(() => import("./pages/sectors/SectorPage"));
const AccountingPage = lazy(() => import("./pages/sectors/AccountingPage"));
const LegalPage = lazy(() => import("./pages/sectors/LegalPage"));
const MultiSitePage = lazy(() => import("./pages/sectors/MultiSitePage"));
const MedicalPage = lazy(() => import("./pages/sectors/MedicalPage"));
const SmeServicesPage = lazy(() => import("./pages/sectors/SmeServicesPage"));
const RegulatedPage = lazy(() => import("./pages/sectors/RegulatedPage"));
const ProblemsHub = lazy(() => import("./pages/problems/ProblemsHub"));
const ProblemPage = lazy(() => import("./pages/problems/ProblemPage"));
const UnstableITPage = lazy(() => import("./pages/problems/UnstableITPage"));
const DataSecurityPage = lazy(() => import("./pages/problems/DataSecurityPage"));
const ProviderIndependencePage = lazy(() => import("./pages/problems/ProviderIndependencePage"));
const SupportReactivityPage = lazy(() => import("./pages/problems/SupportReactivityPage"));
const PredictableCostsPage = lazy(() => import("./pages/problems/PredictableCostsPage"));
const SimplifiedManagementPage = lazy(() => import("./pages/problems/SimplifiedManagementPage"));
const RolesHub = lazy(() => import("./pages/roles/RolesHub"));
const RolePage = lazy(() => import("./pages/roles/RolePage"));
const CEOPage = lazy(() => import("./pages/roles/CEOPage"));
const CFOPage = lazy(() => import("./pages/roles/CFOPage"));
const ITManagerPage = lazy(() => import("./pages/roles/ITManagerPage"));
const PartnerPage = lazy(() => import("./pages/roles/PartnerPage"));
const EditorPage = lazy(() => import("./pages/roles/EditorPage"));
const WhyHub = lazy(() => import("./pages/why/WhyHub"));
const WhyPage = lazy(() => import("./pages/why/WhyPage"));
const VirtualDesktopPage = lazy(() => import("./pages/landing/VirtualDesktopPage"));
const VirtualDesktopSEOPage = lazy(() => import("./pages/solutions/VirtualDesktopSEOPage"));
const HorusPage = lazy(() => import("./pages/hosting/HorusPage"));
const HostingHubPage = lazy(() => import("./pages/hosting/HostingHubPage"));
const MercatorPage = lazy(() => import("./pages/hosting/MercatorPage"));
const SofiskPage = lazy(() => import("./pages/hosting/SofiskPage"));
const AdminDmsPage = lazy(() => import("./pages/hosting/AdminDmsPage"));
const WinPrestPage = lazy(() => import("./pages/hosting/WinPrestPage"));
const WinBooksPage = lazy(() => import("./pages/hosting/WinBooksPage"));
const AdSolutPage = lazy(() => import("./pages/hosting/AdSolutPage"));
const KboPage = lazy(() => import("./pages/hosting/KboPage"));
const ThankYouAudit = lazy(() => import("./pages/ThankYouAudit"));
const GeneralTerms = lazy(() => import("./pages/GeneralTerms"));

// Lazy load non-critical global components
// CustomCursor removed — caused latency issues on some browsers
const CookieConsent = lazy(() => import("./components/CookieConsent").then(m => ({ default: m.CookieConsent })));
const GoogleTagManager = lazy(() => import("./components/GoogleTagManager").then(m => ({ default: m.GoogleTagManager })));
const HelpRequestDialog = lazy(() => import("./components/HelpRequestDialog").then(m => ({ default: m.HelpRequestDialog })));

// Lazy-load skeleton (itself lightweight)
const PageSkeleton = lazy(() => import("./components/PageSkeleton").then(m => ({ default: m.PageSkeleton })));

// Premium loading fallback with skeleton UI
const PageFallback = () => (
  <Suspense fallback={<div className="min-h-screen bg-background" />}>
    <PageSkeleton />
  </Suspense>
);

const FaqCategoryArticleRedirect = () => {
  const { articleSlug } = useParams<{ articleSlug?: string }>();
  return <Navigate to={articleSlug ? `/faq/article/${articleSlug}` : "/faq"} replace />;
};

// Routes wrapped without heavy AnimatePresence
const AnimatedRoutes = () => {
  return (
    <Suspense fallback={<PageFallback />}>
      <Routes>
        <Route path="/" element={<Index />} />
        <Route path="/tarifs" element={<Navigate to="/contact" replace />} />
        <Route path="/services" element={<ServicesHub />} />
        <Route path="/services/hosting" element={<HostingPage />} />
        <Route path="/services/mobility" element={<MobilityPage />} />
        <Route path="/services/security" element={<SecurityPage />} />
        <Route path="/services/support" element={<SupportPage />} />
        <Route path="/services/hardware" element={<HardwarePage />} />
        <Route path="/services/communication" element={<CommunicationPage />} />
        <Route path="/services/backup" element={<BackupPage />} />
        <Route path="/services/continuity" element={<Navigate to="/services/backup" replace />} />
        <Route path="/services/consulting" element={<ConsultingPage />} />
        <Route path="/services/internet" element={<InternetPage />} />
        <Route path="/services/mobile-telephony" element={<MobilePhonePage />} />
        <Route path="/services/teams" element={<TeamsPage />} />
        <Route path="/services/voip" element={<VoIPPage />} />
        <Route path="/services/connectivity" element={<ConnectivityPage />} />
        <Route path="/services/:serviceId" element={<ServicePage />} />
        <Route path="/portail/skycorporate" element={<UnderConstruction />} />
        <Route path="/portail/client" element={<Navigate to="/support/portail-client" replace />} />
        <Route path="/support/portail-client" element={<ClientPortal />} />
        <Route path="/support/base-de-connaissance" element={<Navigate to="/faq" replace />} />
        <Route path="/support/assistance-a-distance" element={<RemoteAssistance />} />
        <Route path="/support/demande" element={<SupportRequest />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/centre-confiance" element={<TrustCenter />} />
        <Route path="/diagnostic-it" element={<DiagnosticIt />} />
        <Route path="/calculateur-cout-it" element={<HiddenItCostCalculator />} />
        <Route path="/comparatif-prestataire-it" element={<ItProviderComparison />} />
        <Route path="/faq/categorie/:categorySlug/:articleSlug" element={<FaqCategoryArticleRedirect />} />
        <Route path="/faq/categorie/:slug" element={<KBCategoryPage />} />
        <Route path="/faq/article/:slug" element={<KBArticlePage />} />
        <Route path="/a-propos/histoire" element={<History />} />
        <Route path="/a-propos/equipe" element={<Team />} />
        <Route path="/a-propos/valeurs" element={<Values />} />
        <Route path="/a-propos/technologies" element={<Technologies />} />
        <Route path="/a-propos/temoignages" element={<ClientTestimonials />} />
        <Route path="/a-propos/articles" element={<Articles />} />
        <Route path="/a-propos/presse" element={<Press />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/recrutement" element={<Recruitment />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/politique-cookies" element={<CookiePolicy />} />
        <Route path="/mentions-legales" element={<LegalNotice />} />
        <Route path="/politique-confidentialite" element={<Navigate to="/confidentialite" replace />} />
        <Route path="/confidentialite" element={<PrivacyPolicy />} />
        <Route path="/cg" element={<GeneralTerms />} />
        <Route path="/secteurs" element={<SectorsHub />} />
        <Route path="/secteurs/comptables-fiduciaires" element={<AccountingPage />} />
        <Route path="/secteurs/cabinets-juridiques" element={<LegalPage />} />
        <Route path="/secteurs/multisites" element={<Navigate to="/secteurs/entreprises-multi-sites" replace />} />
        <Route path="/secteurs/entreprises-multi-sites" element={<MultiSitePage />} />
        <Route path="/secteurs/medical-paramedical" element={<MedicalPage />} />
        <Route path="/secteurs/pme-services" element={<SmeServicesPage />} />
        <Route path="/secteurs/secteurs-reglementes" element={<Navigate to="/secteurs/professions-reglementees" replace />} />
        <Route path="/secteurs/professions-reglementees" element={<RegulatedPage />} />
        <Route path="/secteurs/:slug" element={<SectorPage />} />
        <Route path="/problematiques" element={<ProblemsHub />} />
        <Route path="/problematiques/informatique-instable" element={<UnstableITPage />} />
        <Route path="/problematiques/securite-donnees" element={<DataSecurityPage />} />
        <Route path="/problematiques/prestataire-peu-fiable" element={<ProviderIndependencePage />} />
        <Route path="/problematiques/support-inefficace" element={<Navigate to="/problematiques/support-it-lent" replace />} />
        <Route path="/problematiques/support-it-lent" element={<SupportReactivityPage />} />
        <Route path="/problematiques/couts-imprevisibles" element={<PredictableCostsPage />} />
        <Route path="/problematiques/transformation-digitale" element={<Navigate to="/problematiques/gerer-it-interne" replace />} />
        <Route path="/problematiques/manque-visibilite" element={<Navigate to="/problematiques/gerer-it-interne" replace />} />
        <Route path="/problematiques/gerer-it-interne" element={<SimplifiedManagementPage />} />
        <Route path="/problematiques/:slug" element={<ProblemPage />} />
        <Route path="/votre-besoin" element={<Navigate to="/problematiques" replace />} />
        <Route path="/votre-besoin/informatique-stable" element={<Navigate to="/problematiques/informatique-instable" replace />} />
        <Route path="/votre-besoin/securite-donnees" element={<Navigate to="/problematiques/securite-donnees" replace />} />
        <Route path="/votre-besoin/couts-previsibles" element={<Navigate to="/problematiques/couts-imprevisibles" replace />} />
        <Route path="/pour-qui" element={<RolesHub />} />
        <Route path="/pour-qui/dirigeant-ceo" element={<CEOPage />} />
        <Route path="/pour-qui/directeur-financier-cfo" element={<CFOPage />} />
        <Route path="/pour-qui/responsable-it" element={<ITManagerPage />} />
        <Route path="/pour-qui/devenir-partenaire" element={<PartnerPage />} />
        <Route path="/pour-qui/editeur-logiciel" element={<EditorPage />} />
        <Route path="/pour-qui/partner" element={<Navigate to="/pour-qui/devenir-partenaire" replace />} />
        <Route path="/pour-qui/editor" element={<Navigate to="/pour-qui/editeur-logiciel" replace />} />
        <Route path="/pour-qui/:slug" element={<RolePage />} />
        <Route path="/pourquoi-consultic" element={<WhyHub />} />
        <Route path="/pourquoi-consultic/:slug" element={<WhyPage />} />
        <Route path="/virtual-desktop" element={<VirtualDesktopPage />} />
        <Route path="/solutions/virtual-desktop-pme" element={<VirtualDesktopSEOPage />} />
        <Route path="/hebergement" element={<HostingHubPage />} />
        <Route path="/hebergement/horus" element={<HorusPage />} />
        <Route path="/hebergement/mercator" element={<MercatorPage />} />
        <Route path="/hebergement/sofisk" element={<SofiskPage />} />
        <Route path="/hebergement/admin-dms" element={<AdminDmsPage />} />
        <Route path="/hebergement/winprest" element={<WinPrestPage />} />
        <Route path="/hebergement/winbooks" element={<WinBooksPage />} />
        <Route path="/hebergement/adsolut" element={<AdSolutPage />} />
        <Route path="/hebergement/kbo" element={<KboPage />} />
        <Route path="/merci-audit" element={<ThankYouAudit />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </Suspense>
  );
};

// Defer non-critical global components until after main content is interactive
const DeferredGlobals = () => {
  const [ready, setReady] = useState(false);
  useEffect(() => {
    // Wait for idle time to mount non-critical components
    if (window.requestIdleCallback && window.cancelIdleCallback) {
      const id = window.requestIdleCallback(() => setReady(true), { timeout: 3000 });
      return () => window.cancelIdleCallback?.(id);
    } else {
      const t = setTimeout(() => setReady(true), 1500);
      return () => clearTimeout(t);
    }
  }, []);

  if (!ready) return null;
  return (
    <Suspense fallback={null}>
      <Toaster />
      <Sonner />
      
    </Suspense>
  );
};

const InitialMetadataCleanup = () => {
  useEffect(() => {
    document.querySelectorAll('[data-initial-localized="true"]').forEach((node) => node.remove());
  }, []);

  return null;
};

const App = () => {
  const canonicalLocation = canonicalizeLanguageLocation(
    window.location.pathname,
    window.location.search,
    window.location.hash,
  );
  const currentLocation = `${window.location.pathname}${window.location.search}${window.location.hash}`;
  if (canonicalLocation.url !== currentLocation) {
    window.history.replaceState(window.history.state, '', canonicalLocation.url);
  }

  return (
    <MotionConfig reducedMotion="user">
    <HelmetProvider>
      <InitialMetadataCleanup />
      <LanguageProvider>
        <TooltipProvider>
          <DeferredGlobals />
          <BrowserRouter basename={canonicalLocation.basename || undefined}>
            <ScrollToTop />
            <AnimatedRoutes />
            <Suspense fallback={null}>
              <GoogleTagManager />
              <HelpRequestDialog />
              <CookieConsent />
            </Suspense>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </HelmetProvider>
    </MotionConfig>
  );
};

export default App;
